import {useDispatch, useSelector} from 'react-redux';
import axios from 'axios';
import {getHeaders} from '@core/actions/xhrRequest';
import {hidePreloader, showPreloader} from '@core/actions/PreloaderActions';
import {setUpsalePopupOpen} from '../actions/UpsaleActions';
import {shouldShowUpsale, trackUpsaleImpression} from '../helpers/upsaleStats';
import {getIsShowMap} from "../helpers/fieldRules";
import QueryStorage from "../../../../common/helpers/QueryStorage";
import {redirectViaWorldpayGate} from "@common/helpers/worldpayHppGate";

function useOrderSubmit() {
    const dispatch = useDispatch();
    const {defaultPaymentMethod} = useSelector(state => state.payment);
    const {user} = useSelector(state => state.user);
    const {limits} = useSelector(state => state.orderOptions.data);
    const {balance} = useSelector(state => state.balance);

    const getFormattedValue = (field, values) => {
        const fieldOther = field + 'Other';
        return values[field] === 'Other' ? `${values[field]} / ${values[fieldOther]}` : values[field];
    };

    const getFormData = values => {
        const showMap = getIsShowMap(values, limits, balance);
        const form = new FormData();
        form.append('order[academic_level]', values.academicLevel);
        form.append('order[type_of_paper]', getFormattedValue('typeOfPaper', values));
        form.append('order[subject]', getFormattedValue('subject', values));
        form.append('order[topic]', values.topic || 'Writer\'s choice');
        form.append('order[paper_instructions]', values.instruction || '');
        form.append('order[source]', showMap.source ? values.source : 0);
        form.append('order[paper_format]', showMap.paperFormat ? getFormattedValue('paperFormat', values) : '');
        form.append('order[page]', showMap.page ? values.page : 0);
        form.append('order[slide]', showMap.slide ? values.slide : 0);
        form.append('order[problem]', showMap.problem ? values.problem : 0);
        form.append('order[question]', showMap.question ? values.question : 0);
        form.append('order[visual_piece]', showMap.visualPiece ? values.visualPiece : 0);
        form.append('order[spaced]', showMap.spacing ? values.spacing : 1);
        form.append('order[deadline]', values.deadline);
        form.append('order[exact_deadline_interval]', values.exactDeadline);
        form.append('order[author_category]', values.authorCategory || '');
        form.append('order[is_free_author_category_id]', values.freeAuthorCategory || '');
        form.append('order[plagiarism_report]', showMap.plagiarismReport ? values.plagiarismReport : 0);
        form.append('order[is_free_plagiarism_report]', showMap.freePlagiarismReport ? values.freePlagiarismReport : 0);
        form.append('order[abstract_page]', showMap.abstractPage ? values.abstractPage : 0);
        form.append('order[is_free_abstract_page]', showMap.freeAbstractPage ? values.freeAbstractPage : 0);
        form.append('order[vip_support]', showMap.vipSupport ? values.vipSupport : 0);
        form.append('order[is_free_vip_support]', showMap.freeVipSupport ? values.freeVipSupport : 0);
        form.append('order[used_source]', 1);
        form.append('order[is_free_used_source]', 1);
        form.append('order[editor_check]', showMap.editorCheck ? values.editorCheck : 0);
        form.append('order[is_free_editor_check]', showMap.freeEditorCheck ? values.freeEditorCheck : 0);
        form.append('order[preferred_writer_uid]', values.preferredWriter || '');
        form.append('order[coupon]', values.coupon ? values.coupon.replace(/[^a-zA-Z0-9]/g, '') : '');
        form.append('order[form_type]', values.formType);
        // Upsell funnel: 'accepted' | 'skipped' | '' (modal not involved).
        // essay-db records it into upsell_event, linked to this order.
        form.append('order[upsell_decision]', values.upsellDecision || '');
        form.append('paymentMethod', defaultPaymentMethod);
        form.append('email', user.email);

        // Toggle to apply customer's store credit on this order. BFF reads
        // it at the top level (not under `order[...]`) and chains the
        // upstream apply-balance call inside Payment::process before
        // generating the merchant URL — when SC fully covers the order,
        // the merchant is never contacted.
        if (values.useBalance) {
            form.append('use_balance', '1');
        }

        if (Array.isArray(values.files)) {
            values.files.map((file, index) => {
                form.append(`files[${index}][name]`, file.name);
                form.append(`files[${index}][originalName]`, file.originalName);
                form.append(`files[${index}][size]`, file.size);
                form.append(`files[${index}][mimeType]`, file.mimeType);
            });
        }

        return form;
    };

    const redirectToUrl = url => {
        redirectViaWorldpayGate(url, {
            headers: getHeaders(),
            onFailure: () => location.reload(),
        });
    };

    const sendData = (url, form) => {
        dispatch(showPreloader());
        axios.post(url, form, {headers: getHeaders(), timeout: 60000})
            .then(response => {
                if (response && response.status === 200) {
                    QueryStorage.clear();
                    try {
                        sessionStorage.removeItem('v5_form_values');
                    } catch {
                    }
                }

                if (typeof response.data === 'string') {
                    redirectToUrl(response.data);
                } else if (response.data.redirectUrl) {
                    dispatch(hidePreloader());
                    location.href = response.data.redirectUrl;
                } else if (response.data.url) {
                    redirectToUrl(response.data.url);
                } else {
                    location.reload();
                }
            })
            .catch(error => {
                dispatch(hidePreloader());
                if (error && error.response && error.response.data && error.response.data.redirectTo) {
                    location.href = error.response.data.redirectTo;
                }
            });
    };

    // The only sender. No upsell gate — callers decide whether to show the
    // modal first; once a decision is made this submits unconditionally.
    const submitOrder = values => {
        sendData('/api/orders', getFormData(values));
    };

    // Single decision point for the wizard's final submit: open the upsell
    // modal once, otherwise submit. The modal's own buttons call submitOrder
    // directly, so there is no skip-flag/gate re-entry race.
    const requestPayment = values => {
        if (shouldShowUpsale(values)) {
            trackUpsaleImpression();
            dispatch(setUpsalePopupOpen(true));
            return;
        }
        submitOrder(values);
    };

    // Account create/reorder pages gate the modal in their own click handler,
    // so here createOrder is just an unconditional create.
    const createOrder = submitOrder;

    const updateOrder = (uniqueNumber, values) => {
        const form = getFormData(values);
        form.append('unique_number', uniqueNumber);
        sendData(`/api/order/${uniqueNumber}`, form);
    };

    return {
        createOrder,
        requestPayment,
        submitOrder,
        updateOrder,
    };
}

export default useOrderSubmit;
